import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { willHydrate } from '../hydration';
import { loadLocale } from './locales';
import enUS from './en-US.json';

/** All available locales and their native display names */
export const SUPPORTED_LOCALES: Record<string, string> = {
  'en-US': 'English (US)',
  'en-AU': 'English (AU)',
  'en-CA': 'English (CA)',
  'en-GB': 'English (UK)',
  'ar-SA': 'العربية',
  'bg-BG': 'Български',
  'ca-ES': 'Català',
  'cs-CZ': 'Čeština',
  'cy-GB': 'Cymraeg',
  'da-DK': 'Dansk',
  'de-AT': 'Deutsch (AT)',
  'de-DE': 'Deutsch',
  'es-AR': 'Español (AR)',
  'es-ES': 'Español',
  'es-MX': 'Español (MX)',
  'et-EE': 'Eesti',
  'eu-ES': 'Euskara',
  'fi-FI': 'Suomi',
  'fr-CA': 'Français (CA)',
  'fr-FR': 'Français',
  'ga-IE': 'Gaeilge',
  'gl-ES': 'Galego',
  'he-IL': 'עברית',
  'hi-IN': 'हिन्दी',
  'hr-HR': 'Hrvatski',
  'hu-HU': 'Magyar',
  'id-ID': 'Bahasa Indonesia',
  'is-IS': 'Íslenska',
  'it-IT': 'Italiano',
  'ja-JP': '日本語',
  'ko-KR': '한국어',
  'lt-LT': 'Lietuvių',
  'lv-LV': 'Latviešu',
  'mk-MK': 'Македонски',
  'ms-MY': 'Bahasa Melayu',
  'mt-MT': 'Malti',
  'nl-NL': 'Nederlands',
  'no-NO': 'Norsk',
  'pl-PL': 'Polski',
  'pt-BR': 'Português (BR)',
  'pt-PT': 'Português',
  'ro-RO': 'Română',
  'ru-RU': 'Русский',
  'sk-SK': 'Slovenčina',
  'sl-SI': 'Slovenščina',
  'sq-AL': 'Shqip',
  'sr-RS': 'Српски',
  'sv-SE': 'Svenska',
  'th-TH': 'ไทย',
  'tl-PH': 'Filipino',
  'tr-TR': 'Türkçe',
  'uk-UA': 'Українська',
  'vi-VN': 'Tiếng Việt',
  'zh-CN': '中文 (简体)',
  'zh-TW': '中文 (繁體)',
};

export const LOCALE_ALIASES: Record<string, string> = {
  'ar-SA': 'Arabic',
  'bg-BG': 'Bulgarian',
  'ca-ES': 'Catalan',
  'cs-CZ': 'Czech',
  'cy-GB': 'Welsh',
  'da-DK': 'Danish',
  'de-AT': 'German Austria',
  'de-DE': 'German',
  'es-AR': 'Spanish Argentina',
  'es-ES': 'Spanish',
  'es-MX': 'Spanish Mexico',
  'et-EE': 'Estonian',
  'eu-ES': 'Basque',
  'fi-FI': 'Finnish',
  'fr-CA': 'French Canada',
  'fr-FR': 'French',
  'ga-IE': 'Irish',
  'gl-ES': 'Galician',
  'he-IL': 'Hebrew',
  'hi-IN': 'Hindi',
  'hr-HR': 'Croatian',
  'hu-HU': 'Hungarian',
  'id-ID': 'Indonesian',
  'is-IS': 'Icelandic',
  'it-IT': 'Italian',
  'ja-JP': 'Japanese',
  'ko-KR': 'Korean',
  'lt-LT': 'Lithuanian',
  'lv-LV': 'Latvian',
  'mk-MK': 'Macedonian',
  'ms-MY': 'Malay',
  'mt-MT': 'Maltese',
  'nl-NL': 'Dutch',
  'no-NO': 'Norwegian',
  'pl-PL': 'Polish',
  'pt-BR': 'Portuguese Brazil',
  'pt-PT': 'Portuguese',
  'ro-RO': 'Romanian',
  'ru-RU': 'Russian',
  'sk-SK': 'Slovak',
  'sl-SI': 'Slovenian',
  'sq-AL': 'Albanian',
  'sr-RS': 'Serbian',
  'sv-SE': 'Swedish',
  'th-TH': 'Thai',
  'tl-PH': 'Filipino Tagalog',
  'tr-TR': 'Turkish',
  'uk-UA': 'Ukrainian',
  'vi-VN': 'Vietnamese',
  'zh-CN': 'Chinese Simplified',
  'zh-TW': 'Chinese Traditional',
};

const LOCALE_STORAGE_KEY = 'localingos-locale';

/**
 * URL-based locale resolver. Reads the URL prefix synchronously *before* React
 * renders so the initial render matches the prerendered HTML (no hydration
 * mismatch). Returns the full i18n locale (e.g. "es-ES") or null if no prefix.
 */
function getLocaleFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  const match = window.location.pathname.match(/^\/(es|de|fr|ja|pt-BR|zh-CN|ko)(\/|$)/);
  if (!match) return null;
  const seoToI18n: Record<string, string> = {
    es: 'es-ES', de: 'de-DE', fr: 'fr-FR', ja: 'ja-JP',
    'pt-BR': 'pt-BR', 'zh-CN': 'zh-CN', ko: 'ko-KR',
  };
  return seoToI18n[match[1]] || null;
}

/**
 * Returns the persisted locale from localStorage, falling back to 'en-US'.
 */
function getSavedLocale(): string {
  try {
    const saved = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (saved && SUPPORTED_LOCALES[saved]) {
      return saved;
    }
  } catch {
    // localStorage unavailable (SSR, private browsing, etc.)
  }

  // Auto-detect from browser language
  if (typeof navigator !== 'undefined') {
    const browserLang = navigator.language;
    if (SUPPORTED_LOCALES[browserLang]) return browserLang;
    // Try base language (e.g. "de" → "de-DE")
    const base = browserLang.split('-')[0];
    const match = Object.keys(SUPPORTED_LOCALES).find(k => k.startsWith(base + '-'));
    if (match) return match;
  }

  return 'en-US';
}

/**
 * The language i18next starts in.
 *
 * On a page we are about to hydrate, this has to reproduce exactly what the
 * prerenderer rendered, or the first client render disagrees with the served HTML
 * and React discards the whole snapshot. The prerenderer runs with empty
 * localStorage and an en-US browser, so an unprefixed URL was rendered in en-US —
 * `getSavedLocale()`'s localStorage and navigator.language sniffing would produce
 * something else for, say, a returning German visitor on `/`.
 *
 * Deferring to en-US costs nothing on those routes: LocaleLayout already forces the
 * language to the URL prefix (en-US when there is none) immediately after mount, so
 * the saved locale was never honoured on marketing pages anyway.
 *
 * Client-rendered routes — the dashboard, and anything CloudFront rewrites to the
 * homepage — keep the saved-locale behaviour, since nothing is being adopted there.
 */
function getInitialLocale(): string {
  return getLocaleFromUrl() || (willHydrate() ? 'en-US' : getSavedLocale());
}

/**
 * The language this page load starts in, resolved once at module scope.
 *
 * Held as a constant rather than re-derived because `loadInitialLocale()` has to ask
 * for exactly the catalogue that was passed to `init({ lng })`. Reading
 * `i18n.language` back instead would be subtly wrong: with no catalogue loaded yet
 * i18next may report the resolved fallback (en-US) rather than the requested
 * language, and we would then load English over the top of a German page.
 */
export const INITIAL_LOCALE: string = getInitialLocale();

/**
 * Fetches the catalogue for the initial language.
 *
 * Must be awaited before the first render. i18next was initialised with
 * `lng: INITIAL_LOCALE` but only the en-US resources, so until this resolves `t()`
 * falls through to the fallback and every string comes out in English. Rendering in
 * that window is what would put English text under a `/de` URL — and because the
 * prerenderer snapshots whatever is on screen, it would bake that into the shipped
 * HTML rather than merely flicker.
 *
 * Resolves immediately when the initial language is en-US, which is most loads.
 */
export function loadInitialLocale(): Promise<void> {
  return loadLocale(INITIAL_LOCALE);
}

/**
 * Persist the selected locale to localStorage.
 */
export function saveLocale(locale: string): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // ignore
  }
}

i18n
  .use(initReactI18next)
  .init({
    /*
     * Only the fallback language is bundled. Every other catalogue arrives through
     * ./locales.ts as its own chunk; `loadInitialLocale()` below fetches the one this
     * page actually needs before the first render.
     */
    resources: {
      'en-US': { translation: enUS },
    },
    lng: INITIAL_LOCALE,
    fallbackLng: 'en-US',
    interpolation: {
      escapeValue: false, // React already escapes by default
    },
  });

/*
 * Re-exported so callers that already depend on this module have one place to reach
 * for. Components that must not pull react-i18next in (see the note in ./locales.ts)
 * should import from './locales' directly instead.
 */
export { loadLocale, changeLocale } from './locales';

export default i18n;
